import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaFileExcel, FaFilePdf, FaSearch, FaTimes } from "react-icons/fa";
import Select from "react-select";
import ExportMenu from "./ExportMenu";
import { apiFetch } from "./utils/api";
import { exportTicketsExcel, exportTicketsPdf } from "./utils/ticketExport";
import logo_eagle from "./assets/img/logo_eagle.png";
import "./dSpr.css";
import "./Download_History.css";

const DOC_OPTIONS = [
    { value: "", label: "All documents" },
    { value: "payslip", label: "Payslip" },
    { value: "form16", label: "Form-16" },
    { value: "healthcard", label: "Health card" },
];

// Badge tint per document type.
const DOC_TINT = {
    payslip:    { bg: "#e8f0fb", fg: "#1e5aa8" },
    form16:     { bg: "#efeafd", fg: "#6d3fd4" },
    healthcard: { bg: "#e5f6f2", fg: "#0f766e" },
};

const fmtTs = (d) => {
    if (!d) return "";
    try { return new Date(d).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" }); }
    catch { return String(d); }
};

// react-select sizing - compact 34px control that fills its field.
const selectStyles = {
    container: (b) => ({ ...b, width: "100%" }),
    control: (b, s) => ({ ...b, minHeight: 34, minWidth: 0, borderColor: s.isFocused ? "#3b82f6" : "#cbd5e1", boxShadow: "none", ":hover": { borderColor: "#94a3b8" } }),
    valueContainer: (b) => ({ ...b, padding: "0 8px" }),
    dropdownIndicator: (b) => ({ ...b, padding: 5 }),
    clearIndicator: (b) => ({ ...b, padding: 5 }),
    indicatorSeparator: (b) => ({ ...b, marginTop: 6, marginBottom: 6 }),
    input: (b) => ({ ...b, margin: 0, padding: 0 }),
    menu: (b) => ({ ...b, zIndex: 30 }),
    menuPortal: (b) => ({ ...b, zIndex: 30 }),
};
const pickDate = (e) => { try { e.currentTarget.showPicker && e.currentTarget.showPicker(); } catch { /* not supported */ } };

const Download_History = ({ navSize }) => {
    const navigate = useNavigate();
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [employees, setEmployees] = useState([]);
    const [roles, setRoles] = useState([]);
    const [f, setF] = useState({ emp_id: "", role: "", doc_type: "", from: "", to: "" });

    useEffect(() => {
        fetchloginedEmployees();
        loadFilters();
        load({ emp_id: "", role: "", doc_type: "", from: "", to: "" });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchloginedEmployees = async () => {
        try {
            const res = await apiFetch(`/api/employee/logined_employee`, { method: "GET", credentials: "include" });
            const data = await res.json();
            if (data?.message === "Access Denied. No Token Provided!" || data?.message === "Invalid Token") navigate("/login");
        } catch (e) { console.error("Error fetching employee:", e); }
    };

    const loadFilters = async () => {
        try {
            const res = await apiFetch(`/api/download-history/filters`);
            const d = await res.json();
            setEmployees(d.employees || []);
            setRoles(d.roles || []);
        } catch (e) { console.error("❌ filters:", e); }
    };

    const load = async (filters) => {
        setLoading(true);
        try {
            const qs = new URLSearchParams(Object.entries(filters).filter(([, v]) => v)).toString();
            const res = await apiFetch(`/api/download-history/list${qs ? `?${qs}` : ""}`);
            const data = await res.json();
            setRows(Array.isArray(data) ? data : []);
        } catch (e) {
            console.error("❌ download-history load:", e);
            setRows([]);
        } finally { setLoading(false); }
    };

    const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
    const apply = () => load(f);
    const clear = () => { const z = { emp_id: "", role: "", doc_type: "", from: "", to: "" }; setF(z); load(z); };
    const hasFilters = Object.values(f).some(Boolean);

    // react-select option lists + current-value lookups.
    const empOptions = [{ value: "", label: "All employees" }, ...employees.map((e) => ({ value: e.emp_id, label: `${`${e.emp_first_name} ${e.emp_last_name}`.trim()} (${e.emp_id})` }))];
    const roleOptions = [{ value: "", label: "All roles" }, ...roles.map((r) => ({ value: r, label: r }))];
    const optOf = (opts, v) => opts.find((o) => o.value === v) || opts[0];

    const filterLabel = useMemo(() => {
        const parts = [];
        if (f.emp_id) { const e = employees.find((x) => x.emp_id === f.emp_id); parts.push(`Employee: ${e ? `${e.emp_first_name} ${e.emp_last_name}`.trim() : f.emp_id}`); }
        if (f.role) parts.push(`Role: ${f.role}`);
        if (f.doc_type) parts.push(`Document: ${DOC_OPTIONS.find((o) => o.value === f.doc_type)?.label}`);
        if (f.from) parts.push(`From: ${f.from}`);
        if (f.to) parts.push(`To: ${f.to}`);
        return parts.join("   ·   ") || "All downloads";
    }, [f, employees]);

    // ── Export (reuses the shared ticket exporter) ────────────────────────
    const EXPORT_COLS = [
        { key: "emp",              label: "Employee",      width: 26, pdfWidth: 130 },
        { key: "emp_id",           label: "Emp ID",        width: 14, pdfWidth: 70 },
        { key: "emp_access_level", label: "Role",          width: 16, pdfWidth: 80 },
        { key: "emp_department",   label: "Department",    width: 20, pdfWidth: 90 },
        { key: "doc_label",        label: "Document",      width: 14, pdfWidth: 80 },
        { key: "period_label",     label: "Period",        width: 16, pdfWidth: 80 },
        { key: "downloaded_at",    label: "Downloaded at", width: 24, pdfWidth: 130 },
    ];
    const exportRows = () => rows.map((r) => ({
        emp: `${r.emp_first_name} ${r.emp_last_name}`.trim() || r.emp_id,
        emp_id: r.emp_id,
        emp_access_level: r.emp_access_level || "",
        emp_department: r.emp_department || "",
        doc_label: r.doc_label,
        period_label: r.period_label || "",
        downloaded_at: fmtTs(r.downloaded_at),
    }));
    const stamp = () => new Date().toISOString().slice(0, 10);
    const doExcel = () => exportTicketsExcel({ moduleName: "dSlip", sheetTitle: "Download History", columns: EXPORT_COLS, rows: exportRows(), filterLabel, filename: `Download_History_${stamp()}.xlsx` });
    const doPdf = () => exportTicketsPdf({ moduleName: "dSlip", sheetTitle: "Download History", columns: EXPORT_COLS, rows: exportRows(), filterLabel, filename: `Download_History_${stamp()}.pdf` });

    return (
        <div className="dh-container">
            <div className={`dh-dashboard ${navSize}`}>
                <div className="dspr-header-wrapper">
                    <h2>
                        <a className="navbar-brand" href="#">
                            <img src={logo_eagle} alt="dAdmin Logo" style={{ height: "50px", objectFit: "contain", marginLeft: "-20px" }} />
                        </a>
                        Download History
                    </h2>
                    <p className="dspr-subtitle">Every payslip, Form-16 and health-card download by employees, newest first. Filter by employee, role, document type or date range to spot repeat downloads, then export to Excel or PDF.</p>
                </div>

                <div className="dh-content">
                    {/* Filters */}
                    <div className="dh-filters">
                        <div className="dh-field dh-field-grow">
                            <label>Employee</label>
                            <Select classNamePrefix="dh-sel" styles={selectStyles} menuPortalTarget={document.body}
                                options={empOptions} value={optOf(empOptions, f.emp_id)}
                                onChange={(o) => set("emp_id", o?.value || "")} />
                        </div>
                        <div className="dh-field dh-field-sel">
                            <label>Role</label>
                            <Select classNamePrefix="dh-sel" styles={selectStyles} menuPortalTarget={document.body}
                                options={roleOptions} value={optOf(roleOptions, f.role)}
                                onChange={(o) => set("role", o?.value || "")} />
                        </div>
                        <div className="dh-field dh-field-sel">
                            <label>Document</label>
                            <Select classNamePrefix="dh-sel" styles={selectStyles} menuPortalTarget={document.body}
                                options={DOC_OPTIONS} value={optOf(DOC_OPTIONS, f.doc_type)}
                                onChange={(o) => set("doc_type", o?.value || "")} />
                        </div>
                        <div className="dh-field">
                            <label>From</label>
                            <input type="date" value={f.from} onClick={pickDate} onChange={(e) => set("from", e.target.value)} />
                        </div>
                        <div className="dh-field">
                            <label>To</label>
                            <input type="date" value={f.to} onClick={pickDate} onChange={(e) => set("to", e.target.value)} />
                        </div>
                        <div className="dh-filter-actions">
                            <button className="dh-btn dh-btn-apply" onClick={apply}><FaSearch /> Apply</button>
                            {hasFilters && <button className="dh-btn dh-btn-clear" onClick={clear}><FaTimes /> Clear</button>}
                            <ExportMenu onExcel={doExcel} onPdf={doPdf} disabled={!rows.length} />
                        </div>
                    </div>

                    {/* Count line */}
                    <div className="dh-toolbar">
                        <span className="dh-count">{loading ? "Loading…" : `${rows.length} download${rows.length === 1 ? "" : "s"}`}</span>
                    </div>

                    {/* Table */}
                    <div className="dh-table-wrap">
                        <table className="dh-table">
                            <thead>
                                <tr>
                                    <th>Employee</th>
                                    <th>Role</th>
                                    <th>Department</th>
                                    <th>Document</th>
                                    <th>Period</th>
                                    <th>Downloaded at</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr><td colSpan={6} className="dh-empty">Loading…</td></tr>
                                ) : rows.length === 0 ? (
                                    <tr><td colSpan={6} className="dh-empty">No downloads match these filters.</td></tr>
                                ) : rows.map((r) => {
                                    const tint = DOC_TINT[r.doc_type] || { bg: "#eef1f4", fg: "#475569" };
                                    return (
                                        <tr key={r.id}>
                                            <td>
                                                <div className="dh-emp-cell">
                                                    <div className="dh-avatar">{(r.emp_first_name?.[0] || r.emp_id?.[0] || "?").toUpperCase()}</div>
                                                    <div>
                                                        <div className="dh-emp-name">{`${r.emp_first_name} ${r.emp_last_name}`.trim() || r.emp_id}</div>
                                                        <div className="dh-emp-id">{r.emp_id}</div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td>{r.emp_access_level || "-"}</td>
                                            <td>{r.emp_department || "-"}</td>
                                            <td><span className="dh-doc" style={{ background: tint.bg, color: tint.fg }}>{r.doc_label}</span></td>
                                            <td>{r.period_label || "-"}</td>
                                            <td className="dh-when">{fmtTs(r.downloaded_at)}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Download_History;
